import { useCallback, useEffect, useRef, useState } from 'react'
import { dispatchedActions, store, useAppSelector } from '@dis/redux'
import { selectSelectedTenantId } from '@dis/redux/src/tenants/tenantsSelectors'
import { useRevert } from '@dis/hooks/src/useRevert'
import { useVersionlist, useViewVersion } from '@dis/hooks/src/useVersioning'
import { WsEnhancedResponseMessage, WsMessageModel } from '@dis/types/src/wsModels'
import { useTenantPersona } from '@dis/hooks'
import { selectSelectedPersonaId } from '@dis/redux/src/personas/personasSelectors'
import { selectSelectedJourneyId } from '@dis/redux/src/journeys/journeysSelectors'
import { PersonaChangeNotification, PersonaDetailType } from '@dis/types/src/PersonaTypes'
import { JourneyData, JourneySideloads, JourneySubscribe } from '../journey/grid/types'

type UseRevisionControlVersionProps = {
  handleCloseForMenuItem: VoidFunction
  isJourney: boolean
  isPersona: boolean
  semaphore: boolean
}

export const useRevisionControlVersion = ({
  isPersona,
  isJourney,
  handleCloseForMenuItem,
  semaphore,
}: UseRevisionControlVersionProps) => {
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<number>()
  const journeyDataBackup = useRef<JourneyData>()

  const onPersonaDataReceived = useCallback(
    (data?: WsEnhancedResponseMessage<PersonaDetailType | PersonaChangeNotification>) => {
      if (data?.backenable !== undefined) {
        dispatchedActions.personas.setIsBackEnable(data.backenable)
      }
      if (data?.forwardenable !== undefined) {
        dispatchedActions.personas.setIsForwardEnable(data.forwardenable)
      }
    },
    [],
  )

  // todo: this should be somewhere else (similar to useJourneyCore.tsx)
  useTenantPersona({
    callbacks: {
      change: onPersonaDataReceived,
      revert: onPersonaDataReceived,
      subscribe: onPersonaDataReceived,
    },
    semaphore: isPersona,
  })

  const selectedPersonaId = useAppSelector(selectSelectedPersonaId)
  const selectedTenantId = useAppSelector(selectSelectedTenantId)
  const selectedJourneyId = useAppSelector(selectSelectedJourneyId)

  const { data: versionHistory, loading: isVersionListLoading } = useVersionlist({
    isJourney,
    isPersona,
    semaphore,
  })

  const { send: sendRevert } = useRevert()

  const onVersionDataReceived = useCallback(
    (newData: WsEnhancedResponseMessage<PersonaDetailType | JourneySubscribe>) => {
      if (isPersona) {
        dispatchedActions.personas.setPersonaSelectedVersionData(newData.data as PersonaDetailType)
      } else {
        const newState: JourneyData = {
          sideloads: newData?.sideloads || [],
          subscribe: (newData as WsEnhancedResponseMessage<JourneySubscribe>)?.data || {},
          version: newData?.version || 0,
        }

        if (!journeyDataBackup.current) {
          journeyDataBackup.current = store.getState().journeys.journeyData
        }

        dispatchedActions.journeys.setJourneysStructure(undefined)
        dispatchedActions.journeys.setJourneyData(newState)
      }

      dispatchedActions.journeys.setIsHistoryVersionPreview(true)
      dispatchedActions.personas.setIsHistoryVersionPreview(true)

      if (newData.backenable) {
        dispatchedActions.journeys.setIsBackEnable(newData.backenable)
        dispatchedActions.personas.setIsBackEnable(newData.backenable)
      }
      if (newData.forwardenable) {
        dispatchedActions.journeys.setIsForwardEnable(newData.forwardenable)
        dispatchedActions.personas.setIsForwardEnable(newData.forwardenable)
      }
    },
    [isPersona],
  )

  const { sendViewVersion, viewVersionData, resetViewVersion } = useViewVersion({
    onData: onVersionDataReceived,
  })

  const onClickSetVersion = useCallback(
    (versionIndex: number) => () => {
      handleCloseForMenuItem()
      setSelectedVersionIndex(versionIndex)
      dispatchedActions.journeys.setCanEdit(false)
      dispatchedActions.journeys.setHistoryVersionPreviewMode('time')
      dispatchedActions.personas.setHistoryVersionPreviewMode('time')

      sendViewVersion({
        tenantId: selectedTenantId || 0,
        version: {
          data: versionIndex,
          id: isPersona ? selectedPersonaId?.toString() : selectedJourneyId?.toString(),
          model: isPersona ? WsMessageModel.Persona : WsMessageModel.Document,
        },
      })
    },
    [
      handleCloseForMenuItem,
      isPersona,
      selectedJourneyId,
      selectedPersonaId,
      selectedTenantId,
      sendViewVersion,
    ],
  )

  const onClickSetVersionBack = useCallback(
    (versionIndex?: number) => () => {
      const version = viewVersionData ? viewVersionData?.version : versionIndex
      if (version) {
        setSelectedVersionIndex(version - 1)
        dispatchedActions.journeys.setCanEdit(false)
        dispatchedActions.journeys.setHistoryVersionPreviewMode('backForward')
        dispatchedActions.personas.setHistoryVersionPreviewMode('backForward')

        sendViewVersion({
          tenantId: selectedTenantId || 0,
          version: {
            data: version - 1,
            id: isPersona ? selectedPersonaId?.toString() : selectedJourneyId?.toString(),
            model: isPersona ? WsMessageModel.Persona : WsMessageModel.Document,
          },
        })
      }
    },
    [
      isPersona,
      selectedJourneyId,
      selectedPersonaId,
      selectedTenantId,
      sendViewVersion,
      viewVersionData,
    ],
  )

  const onClickSetVersionForward = useCallback(() => {
    const version = viewVersionData ? viewVersionData?.version : undefined
    if (version) {
      setSelectedVersionIndex(version + 1)
      dispatchedActions.journeys.setCanEdit(false)
      dispatchedActions.journeys.setHistoryVersionPreviewMode('backForward')
      dispatchedActions.personas.setHistoryVersionPreviewMode('backForward')

      sendViewVersion({
        tenantId: selectedTenantId || 0,
        version: {
          data: version + 1,
          id: isPersona ? selectedPersonaId?.toString() : selectedJourneyId?.toString(),
          model: isPersona ? WsMessageModel.Persona : WsMessageModel.Document,
        },
      })
    }
  }, [
    isPersona,
    selectedJourneyId,
    selectedPersonaId,
    selectedTenantId,
    sendViewVersion,
    viewVersionData,
  ])

  const acceptSelectedVersion = useCallback(() => {
    dispatchedActions.personas.setPersonaSelectedVersionData(undefined)
    dispatchedActions.personas.setHistoryVersionPreviewMode(undefined)
    dispatchedActions.journeys.setCanEdit(true)
    dispatchedActions.journeys.setHistoryVersionPreviewMode(undefined)

    setSelectedVersionIndex(undefined)

    sendRevert(
      {
        revert: {
          data: selectedVersionIndex,
          id: isPersona ? selectedPersonaId?.toString() : selectedJourneyId?.toString(),
          model: isPersona ? WsMessageModel.Persona : WsMessageModel.Document,
        },
        tenantId: selectedTenantId || 0,
      },
      () => {
        if (isJourney) {
          dispatchedActions.journeys.setForceRefetchFlag(true)
        }
      },
    )
    if (isJourney) {
      dispatchedActions.journeys.setJourneysStructure(undefined)
      journeyDataBackup.current = undefined
    }

    dispatchedActions.journeys.setIsHistoryVersionPreview(false)
    dispatchedActions.personas.setIsHistoryVersionPreview(false)
    resetViewVersion()
  }, [
    isJourney,
    isPersona,
    resetViewVersion,
    selectedJourneyId,
    selectedPersonaId,
    selectedTenantId,
    selectedVersionIndex,
    sendRevert,
  ])

  const declineSelectedVersion = useCallback(() => {
    setSelectedVersionIndex(undefined)
    dispatchedActions.journeys.setCanEdit(true)
    dispatchedActions.journeys.setHistoryVersionPreviewMode(undefined)

    dispatchedActions.personas.setPersonaSelectedVersionData(undefined)
    dispatchedActions.personas.setIsHistoryVersionPreview(false)
    dispatchedActions.personas.setHistoryVersionPreviewMode(undefined)

    if (isJourney && journeyDataBackup.current) {
      dispatchedActions.journeys.setJourneysStructure(undefined)
      dispatchedActions.journeys.setJourneyData(journeyDataBackup.current)
      journeyDataBackup.current = undefined
      dispatchedActions.journeys.setIsHistoryVersionPreview(false)
      dispatchedActions.journeys.setForceRefetchFlag(true)
    }

    resetViewVersion()
  }, [isJourney, resetViewVersion])

  useEffect(() => {
    if (isJourney) {
      if (viewVersionData?.data && viewVersionData?.version && viewVersionData?.userId) {
        const journeySubscribe = viewVersionData?.data as JourneySubscribe

        const journeySideloads = viewVersionData?.sideloads as JourneySideloads[] | undefined

        // Signature of received data and storing it
        dispatchedActions.journeys.setJourneySignature({
          userId: viewVersionData?.userId,
          version: viewVersionData?.version,
        })

        // Set undefined to force journey re-render
        dispatchedActions.journeys.setJourneysStructure(undefined)

        dispatchedActions.journeys.setJourneyData({
          sideloads: journeySideloads,
          subscribe: journeySubscribe,
          version: viewVersionData.version,
        })
      }
    }

    if (viewVersionData) {
      if (viewVersionData.backenable !== undefined) {
        dispatchedActions.journeys.setIsBackEnable(viewVersionData.backenable)
        dispatchedActions.personas.setIsBackEnable(viewVersionData.backenable)
      }

      if (viewVersionData.forwardenable !== undefined) {
        dispatchedActions.journeys.setIsForwardEnable(viewVersionData.forwardenable)
        dispatchedActions.personas.setIsForwardEnable(viewVersionData.forwardenable)
      }
    }
  }, [isJourney, viewVersionData])

  return {
    acceptSelectedVersion,
    declineSelectedVersion,
    isVersionListLoading,
    onClickSetVersion,
    onClickSetVersionBack,
    onClickSetVersionForward,
    selectedVersionIndex,
    versions: versionHistory?.versions || [],
  }
}
